import React from "react";
import TimelineIcon from "@mui/icons-material/Timeline";
import SpeedIcon from "@mui/icons-material/Speed";

const SPM_LABEL_BY_KIND = Object.freeze({
  forehand: ["fh_per_minute", "Forehands / min"],
  forehand_topspin: ["fh_topspin_per_minute", "FH topspin / min"],
  backhand: ["bh_per_minute", "Backhands / min"],
  backhand_topspin: ["bh_topspin_per_minute", "BH topspin / min"],
});

export function spmLabelForRecipeKind(recipeKind, t) {
  const pair = SPM_LABEL_BY_KIND[recipeKind];
  if (pair) return t(pair[0], pair[1]);
  return t("strokes_per_minute", "Strokes / min");
}

function isComboMetrics(metrics) {
  if (!metrics) return false;
  if (metrics.recipe_combo) return true;
  const kinds = metrics.kinds;
  if (Array.isArray(kinds) && kinds.includes("combo_iterations_per_minute")) {
    return true;
  }
  const comboMin = Number(metrics.combo_iterations_per_minute);
  const comboMax = Number(metrics.combo_max_rally);
  return (
    (Number.isFinite(comboMin) && comboMin > 0) ||
    (Number.isFinite(comboMax) && comboMax > 0)
  );
}

const STROKE_COUNT_TILES = Object.freeze([
  ["fh_count", "fh_count", "Forehands"],
  ["fh_topspin_count", "fh_topspin_count", "Forehand topspins"],
  ["bh_count", "bh_count", "Backhands"],
  ["bh_topspin_count", "bh_topspin_count", "Backhand topspins"],
]);

function isMultiballMetrics(metrics) {
  if (!metrics) return false;
  if (metrics.metric_pack === "multiball") return true;
  const kinds = metrics.kinds;
  if (Array.isArray(kinds) && kinds.includes("total_stroke_count")) {
    return (
      metrics.source === "multiball_pack" ||
      String(metrics.exercise_type || "").toLowerCase().includes("multiball")
    );
  }
  return false;
}

function isServeMetrics(metrics) {
  if (!metrics) return false;
  if (
    Array.isArray(metrics.kinds) &&
    metrics.kinds.some((k) => String(k).startsWith("serve_"))
  ) {
    return true;
  }
  return ["serve_try_count", "serve_short_count", "serve_long_count", "serve_missed_count"].some(
    (k) => metrics[k] != null && Number.isFinite(Number(metrics[k]))
  );
}

function countInclZero(v) {
  if (v == null || !Number.isFinite(Number(v))) return 0;
  return Math.max(0, Math.round(Number(v)));
}

/**
 * Exercise performance metrics tiles.
 * Single-stroke recipes: SPM + max rally.
 * Combo recipes (2-2): iterations / min + max rally iterations.
 */
export function ExerciseCardDefaultMetrics({
  metrics = null,
  t,
  className = "",
  showWhenEmpty = false,
}) {
  /** Positive finite integers — 0 / null / NaN → null (hidden). */
  const positiveOrNull = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return null;
    const n = Math.round(Number(v));
    return n > 0 ? n : null;
  };

  const positiveRate = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return null;
    const n = Math.round(Number(v) * 10) / 10;
    return n > 0 ? n : null;
  };

  const comboMin = positiveRate(metrics?.combo_iterations_per_minute);
  const comboMax = positiveOrNull(metrics?.combo_max_rally);
  const spm = positiveRate(metrics?.strokes_per_minute);
  const maxRally = positiveOrNull(metrics?.max_rally);
  const useServe = isServeMetrics(metrics);
  const useMultiball = !useServe && isMultiballMetrics(metrics);
  const useCombo = !useMultiball && isComboMetrics(metrics);

  const tiles = [];
  if (useServe) {
    tiles.push(
      {
        key: "serve_try_count",
        variant: "strokes",
        label: t("attempts", "Attempts"),
        value: countInclZero(metrics?.serve_try_count),
        Icon: SpeedIcon,
      },
      {
        key: "serve_short_count",
        variant: "max_rally",
        label: t("serve_short_count", "Short"),
        value: countInclZero(metrics?.serve_short_count),
        Icon: TimelineIcon,
      },
      {
        key: "serve_long_count",
        variant: "strokes",
        label: t("serve_long_count", "Long"),
        value: countInclZero(metrics?.serve_long_count),
        Icon: SpeedIcon,
      },
      {
        key: "serve_missed_count",
        variant: "max_rally",
        label: t("serve_missed_count", "Missed"),
        value: countInclZero(metrics?.serve_missed_count),
        Icon: TimelineIcon,
      }
    );
  } else if (useMultiball) {
    tiles.push({
      key: "total_stroke_count",
      variant: "strokes",
      label: t("attempts", "Attempts"),
      value: countInclZero(metrics?.total_stroke_count),
      Icon: SpeedIcon,
    });
    const allowed = Array.isArray(metrics?.kinds) ? new Set(metrics.kinds) : null;
    for (const [key, i18nKey, fallback] of STROKE_COUNT_TILES) {
      if (allowed && !allowed.has(key)) continue;
      const n = positiveOrNull(metrics?.[key]);
      if (n == null) continue;
      tiles.push({
        key,
        variant: "max_rally",
        label: t(i18nKey, fallback),
        value: n,
        Icon: TimelineIcon,
      });
    }
    if (comboMin != null) {
      tiles.push({
        key: "combo_iterations_per_minute",
        variant: "strokes",
        label: t("combo_iterations_per_minute", "Iterations Nb/min"),
        value: comboMin,
        Icon: SpeedIcon,
      });
    }
    if (comboMax != null) {
      tiles.push({
        key: "combo_max_rally",
        variant: "max_rally",
        label: t("combo_max_rally", "Max rally iterations"),
        value: comboMax,
        Icon: TimelineIcon,
      });
    }
  } else if (useCombo) {
    if (comboMin != null) {
      tiles.push({
        key: "combo_iterations_per_minute",
        variant: "strokes",
        label: t("combo_iterations_per_minute", "Iterations Nb/min"),
        value: comboMin,
        Icon: SpeedIcon,
      });
    }
    if (comboMax != null) {
      tiles.push({
        key: "combo_max_rally",
        variant: "max_rally",
        label: t("combo_max_rally", "Max rally iterations"),
        value: comboMax,
        Icon: TimelineIcon,
      });
    }
  } else {
    if (spm != null) {
      tiles.push({
        key: "strokes_per_minute",
        variant: "strokes",
        label: spmLabelForRecipeKind(metrics?.recipe_kind, t),
        value: spm,
        Icon: SpeedIcon,
      });
    }
    if (maxRally != null) {
      tiles.push({
        key: "max_rally",
        variant: "max_rally",
        label: t("max_rally", "Max rally"),
        value: maxRally,
        Icon: TimelineIcon,
      });
    }
  }

  const visible = tiles.slice(0, useServe || useMultiball ? 4 : 2);
  if (!visible.length) {
    if (!showWhenEmpty) return null;
    return null;
  }

  const cardClass = [
    "player-exercise-details__hr-summary-card",
    "player-exercise-details__default-metrics-card",
    useServe ? "player-exercise-details__default-metrics-card--serve" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cardClass} data-metrics="default-exercise">
      {visible.map((tile) => {
        const Icon = tile.Icon;
        return (
          <div
            key={tile.key}
            className={`player-exercise-details__hr-subcard player-exercise-details__hr-subcard--${tile.variant}`}
          >
            <div className="player-exercise-details__hr-subcard-header">
              <Icon
                className={`player-exercise-details__hr-subcard-icon player-exercise-details__hr-subcard-icon--${tile.variant}`}
                style={{ fontSize: 14 }}
              />
              <span>{tile.label}</span>
            </div>
            <div className="player-exercise-details__hr-subcard-value">
              {tile.value}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ExerciseCardDefaultMetrics;
